import { useContext, useMemo, useRef } from 'react';
import { useSubscription } from 'use-subscription';
import { RetilContext } from '../../environment';
import { getDefaultStore } from '../../store';
import { shallowCompare } from '../../utils/shallowCompare';
import { getDefaultCacheModel } from '../defaults';
export function useResource(resource, options) {
    if (options === void 0) { options = {}; }
    var reactContext = useContext(RetilContext);
    if (typeof options === 'string' || typeof options === 'number') {
        options = { vars: options };
    }
    var _a = options.cacheModel, cacheModelOption = _a === void 0 ? reactContext.resourceCacheModel : _a, _b = options.store, storeOption = _b === void 0 ? reactContext.store : _b, _c = options.context, context = _c === void 0 ? reactContext.context : _c, pause = options.pause, policy = options.policy;
    var cacheModel = cacheModelOption || getDefaultCacheModel();
    var store = storeOption || getDefaultStore();
    // Memoize props for the cacheModel so that the same cache will be returned
    // across renders.
    var latestContext = useRef(context);
    var latestStore = useRef(store);
    var latestCacheModelProps = useRef({
        context: context,
        store: store,
    });
    if (!shallowCompare(context, latestContext.current) ||
        store !== latestStore.current) {
        latestCacheModelProps.current = { context: context, store: store };
    }
    latestContext.current = context;
    latestStore.current = store;
    var latestVars = useRef(options.vars);
    if (!shallowCompare(options.vars, latestVars.current)) {
        latestVars.current = options.vars;
    }
    var vars = latestVars.current;
    var cache = cacheModel(latestCacheModelProps.current);
    var service = useMemo(function () { return resource && cache.request(resource, { pause: pause, policy: policy, vars: vars }); }, [cache, pause, policy, resource, vars]);
    var source = (service ? service[0] : nullSource);
    var controller = service && service[1];
    var snapshot = useSubscription(source);
    var controllerWithSource = useMemo(function () { return controller && Object.assign(controller, { source: source }); }, [controller, source]);
    if (resource === null) {
        return [
            {
                abandoned: true,
                get data() {
                    throw new Error("Resource Error: no data is available for a \"null\" resource.");
                },
                get rejection() {
                    throw new Error("Resource Error: no rejection is available for a \"null\" resource.");
                },
                hasData: false,
                hasRejection: false,
                invalidated: false,
                pending: false,
                primed: true,
                id: undefined,
                bucket: undefined,
                vars: vars,
            },
            null,
        ];
    }
    else {
        return [snapshot, controllerWithSource];
    }
}
var nullSource = {
    getCurrentValue: function () { return null; },
    subscribe: function () { return function () { }; },
};
//# sourceMappingURL=useResource.js.map